import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import WarehouseDrawer from "./WarehousesForm"; // Unified drawer for adding/editing warehouse
import API from "../../api"; // API instance for requests
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";

export default function Warehouses() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseToDelete, setWarehouseToDelete] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({ currentPage: 1, pageSize: 5 });

  // Fetch warehouse data from the server
  const getWarehouses = async () => {
    try {
      const response = await API.get("/warehouse");
      setWarehouses(response?.data?.warehouses);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch warehouses when the component mounts
  useEffect(() => {
    const name = JSON.parse(localStorage.getItem("userData")).role;
    getWarehouses();
  }, []);

  const handleDeleteClick = (warehouse) => {
    setWarehouseToDelete(warehouse);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteWarehouse = async () => {
    try {
      await API.delete(`/warehouse/${warehouseToDelete._id}`);
      setWarehouses((prev) =>
        prev.filter((warehouse) => warehouse._id !== warehouseToDelete._id)
      );
      message.success("Warehouse deleted successfully!");
    } catch (err) {
      console.error("Error deleting warehouse:", err);
      message.error(err.response.data.message);
    } finally {
      setIsDeleteModalVisible(false);
    }
  };

  const handleAddWarehouse = async (newWarehouse) => {
    try {
      setOpenDrawer(false);
      getWarehouses();
    } catch (err) {
      console.error(err);
      message.error(err.response.data.message);
    }
  };

  const handleEditWarehouse = async (updatedWarehouse) => {
    try {
      getWarehouses();
      setOpenDrawer(false);
      getWarehouses();
    } catch (err) {
      console.error(err);
      message.error(err.response.data.message);
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.currentPage - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Warehouse Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location) => location.address,
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
    },
    {
      title: "Current Stock",
      dataIndex: "currentStock",
      key: "currentStock",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setIsEdit(true);
              setSelectedWarehouse(record);
              setOpenDrawer(true);
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            style={{ marginLeft: 8 }}
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsEdit(false);
            setSelectedWarehouse(null);
            setOpenDrawer(true);
          }}
        >
          Add New Warehouse
        </Button>
      </div>

      {/* Unified drawer for add/edit */}
      {openDrawer && (
        <WarehouseDrawer
          open={openDrawer}
          setOpen={setOpenDrawer}
          data={selectedWarehouse}
          isEdit={isEdit}
          onSubmit={isEdit ? handleEditWarehouse : handleAddWarehouse}
        />
      )}

      <CustomTable
        columns={columns}
        dataSource={warehouses}
        onPaginationChange={(pagination) =>
          setPaginationInfo({ currentPage: pagination.current, pageSize: pagination.pageSize })
      }
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteWarehouse}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this warehouse? This action cannot be undone."
      />
    </div>
  );
}
